import { withoutAuthErrorParams } from '@/lib/auth/auth-error';
import { mintLocalDevToken } from '@/lib/auth/local-dev-token';
import { type AuthUser, useAuthStore } from '@/lib/auth/store';

const TOKEN_ENDPOINT = '/api/auth/token';
const LOGIN_ENDPOINT = '/api/auth/login';
// 10-minute token TTL (functions/api/auth/token.ts) — refresh 2 minutes early for headroom.
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

interface TokenResponse {
  token: string;
  /** epoch ms */
  expiresAt: number;
  user: AuthUser;
}

// Module-level singleton — auth-gate.tsx and client.ts's 401 handler both drive this directly.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<TokenResponse | null> | null = null;
// Prevents a stale in-flight fetch from re-arming the loop after stopTokenRefreshLoop().
let stopped = false;

/** Redirects to the active IdP via `/api/auth/login`, preserving the current path as `returnTo`. Centralized so every "no valid session" path agrees. */
export const redirectToLogin = (): void => {
  // Drop the one-shot ?authError= flag — carried into returnTo it survives a successful
  // sign-in and keeps re-reporting a long-dead failure on every later 401.
  const query = withoutAuthErrorParams(new URLSearchParams(window.location.search)).toString();
  const returnTo = `${window.location.pathname}${query ? `?${query}` : ''}`;
  window.location.assign(`${LOGIN_ENDPOINT}?returnTo=${encodeURIComponent(returnTo)}`);
};

// Never throws — a fetch rejection or malformed body is just as much "no valid session" as a 401.
const fetchToken = async (): Promise<TokenResponse | null> => {
  const localDevToken = await mintLocalDevToken();
  if (localDevToken) return localDevToken;

  try {
    const response = await fetch(TOKEN_ENDPOINT, { credentials: 'same-origin' });
    if (!response.ok) return null;
    return (await response.json()) as TokenResponse;
  } catch {
    return null;
  }
};

const scheduleNextRefresh = (expiresAt: number): void => {
  // clearTimeout-before-setTimeout: safe under StrictMode's double-invoke and concurrent callers.
  if (refreshTimer !== null) clearTimeout(refreshTimer);
  const delay = Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, 0);
  refreshTimer = setTimeout(() => {
    // Fire-and-forget: a silently dead loop would leave the UI looking authenticated against a gone session.
    void refreshTokenNow().then((ok) => {
      if (!ok) redirectToLogin();
    });
  }, delay);
};

/** Dedupes concurrent callers onto one in-flight request. `false` = no valid session. */
export const refreshTokenNow = async (): Promise<boolean> => {
  stopped = false; // an explicit call means "we want the loop live" — supersedes any earlier stop
  inFlight ??= fetchToken().finally(() => {
    inFlight = null;
  });

  const result = await inFlight;
  if (stopped) return false; // stopTokenRefreshLoop() may have fired while this was in flight
  if (!result) return false;

  useAuthStore.getState().setOidcSession(result.token, result.expiresAt, result.user);
  scheduleNextRefresh(result.expiresAt);
  return true;
};

/** Call once from auth-gate.tsx on mount. Resolves with whether a valid session was found. */
export const startTokenRefreshLoop = (): Promise<boolean> => refreshTokenNow();

/** Call on unmount/sign-out. */
export const stopTokenRefreshLoop = (): void => {
  stopped = true;
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};
